import React, { useCallback, useRef, useEffect } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Info,
  FileOutput,
  Terminal,
  Bug,
  Trash2,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { useScriptEditorStore } from '../../store/scriptEditorStore';
import { BottomTab, DiagnosticMessage, OutputMessage } from '../../../core/types/script-editor.types';
import { cn } from '../../utils/cn';

interface BottomTabButtonProps {
  tab: BottomTab;
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
  badge?: number;
  badgeType?: 'error' | 'warning' | 'info';
}

const BottomTabButton: React.FC<BottomTabButtonProps> = ({
  label,
  icon,
  isActive,
  onClick,
  badge,
  badgeType,
}) => (
  <button
    onClick={onClick}
    className={cn(
      'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-b-2',
      isActive
        ? 'text-zinc-100 border-blue-500 bg-zinc-800'
        : 'text-zinc-500 border-transparent hover:text-zinc-300 hover:bg-zinc-800/50'
    )}
  >
    {icon}
    <span>{label}</span>
    {badge !== undefined && badge > 0 && (
      <span
        className={cn(
          'min-w-[18px] h-[18px] px-1 text-[10px] font-medium rounded-full flex items-center justify-center',
          badgeType === 'error' && 'bg-red-500/20 text-red-400',
          badgeType === 'warning' && 'bg-yellow-500/20 text-yellow-400',
          badgeType === 'info' && 'bg-blue-500/20 text-blue-400',
          !badgeType && 'bg-zinc-700 text-zinc-300'
        )}
      >
        {badge > 99 ? '99+' : badge}
      </span>
    )}
  </button>
);

// Problems Panel Content
const ProblemsPanel: React.FC = () => {
  const { diagnostics, setActiveTab, files, clearDiagnostics } = useScriptEditorStore();

  const handleDiagnosticClick = (diagnostic: DiagnosticMessage) => {
    setActiveTab(diagnostic.fileId);
  };

  const getSeverityIcon = (severity: DiagnosticMessage['severity']) => {
    switch (severity) {
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />;
      case 'info':
        return <Info className="w-4 h-4 text-blue-400 flex-shrink-0" />;
      default:
        return <Info className="w-4 h-4 text-zinc-400 flex-shrink-0" />;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-2 py-1 border-b border-zinc-800">
        <span className="text-xs text-zinc-500">
          {diagnostics.length} problem{diagnostics.length !== 1 ? 's' : ''}
        </span>
        <div className="flex-1" />
        <button
          onClick={clearDiagnostics}
          disabled={diagnostics.length === 0}
          className={cn(
            'p-1 rounded transition-colors',
            diagnostics.length > 0
              ? 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
              : 'text-zinc-600 cursor-not-allowed'
          )}
          title="Clear All"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Problems List */}
      <div className="flex-1 overflow-y-auto">
        {diagnostics.length > 0 ? (
          <table className="w-full text-sm">
            <tbody>
              {diagnostics.map((diagnostic) => {
                const file = files[diagnostic.fileId];
                const fileName = file?.name || diagnostic.filePath.split('/').pop() || 'Unknown';

                return (
                  <tr
                    key={diagnostic.id}
                    onClick={() => handleDiagnosticClick(diagnostic)}
                    className="hover:bg-zinc-800/50 cursor-pointer border-b border-zinc-800/50"
                  >
                    <td className="py-1.5 px-2 w-6">
                      {getSeverityIcon(diagnostic.severity)}
                    </td>
                    <td className="py-1.5 px-2 text-zinc-300">
                      {diagnostic.message}
                    </td>
                    <td className="py-1.5 px-2 text-zinc-500 text-right whitespace-nowrap">
                      {diagnostic.source && (
                        <span className="mr-2">[{diagnostic.source}]</span>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-zinc-500 text-right whitespace-nowrap">
                      {fileName}
                    </td>
                    <td className="py-1.5 px-2 text-zinc-600 text-right whitespace-nowrap w-20">
                      ({diagnostic.line}:{diagnostic.column})
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="flex items-center justify-center h-full text-zinc-500 text-sm">
            No problems detected
          </div>
        )}
      </div>
    </div>
  );
};

// Output Panel Content
const OutputPanel: React.FC = () => {
  const { outputMessages, clearOutput } = useScriptEditorStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [outputMessages.length]);

  const getMessageIcon = (type: OutputMessage['type']) => {
    switch (type) {
      case 'error':
        return <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />;
      case 'success':
        return <Info className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />;
      default:
        return <Info className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />;
    }
  };

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-2 py-1 border-b border-zinc-800">
        <span className="text-xs text-zinc-500">Output</span>
        <div className="flex-1" />
        <button
          onClick={clearOutput}
          disabled={outputMessages.length === 0}
          className={cn(
            'p-1 rounded transition-colors',
            outputMessages.length > 0
              ? 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
              : 'text-zinc-600 cursor-not-allowed'
          )}
          title="Clear Output"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Output Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto font-mono text-xs">
        {outputMessages.length > 0 ? (
          <div className="p-2 space-y-1">
            {outputMessages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'flex items-start gap-2 py-0.5',
                  message.type === 'error' && 'text-red-400',
                  message.type === 'warning' && 'text-yellow-400',
                  message.type === 'success' && 'text-green-400',
                  message.type === 'info' && 'text-zinc-300'
                )}
              >
                {getMessageIcon(message.type)}
                <span className="text-zinc-500">[{formatTime(message.timestamp)}]</span>
                <span className="text-zinc-500">[{message.source}]</span>
                <span>{message.message}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-zinc-500 text-sm">
            No output
          </div>
        )}
      </div>
    </div>
  );
};

// Debug Console Panel Content
const DebugConsolePanel: React.FC = () => {
  return (
    <div className="flex items-center justify-center h-full text-zinc-500">
      <div className="text-center">
        <Bug className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
        <span className="text-sm">Debug Console</span>
        <p className="text-xs text-zinc-600 mt-1">Start debugging to use the console</p>
      </div>
    </div>
  );
};

// Terminal Panel Content
const TerminalPanel: React.FC = () => {
  return (
    <div className="flex items-center justify-center h-full text-zinc-500">
      <div className="text-center">
        <Terminal className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
        <span className="text-sm">Terminal</span>
        <p className="text-xs text-zinc-600 mt-1">Integrated terminal coming soon</p>
      </div>
    </div>
  );
};

export const BottomTabs: React.FC = () => {
  const {
    activeBottomTab,
    setActiveBottomTab,
    diagnosticCounts,
    bottomPanelHeight,
    setBottomPanelHeight,
    toggleBottomPanel,
  } = useScriptEditorStore();

  const resizeRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;

    const startY = e.clientY;
    const startHeight = bottomPanelHeight;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      const deltaY = startY - e.clientY;
      setBottomPanelHeight(startHeight + deltaY);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  }, [bottomPanelHeight, setBottomPanelHeight]);

  return (
    <div className="flex flex-col h-full bg-zinc-900 border-t border-zinc-800">
      {/* Resize Handle */}
      <div
        ref={resizeRef}
        onMouseDown={handleResizeStart}
        className="h-1 bg-zinc-800 hover:bg-blue-500 cursor-row-resize transition-colors"
      />

      {/* Tab Bar */}
      <div className="flex items-center border-b border-zinc-800">
        <BottomTabButton
          tab="problems"
          label="Problems"
          icon={<AlertCircle className="w-4 h-4" />}
          isActive={activeBottomTab === 'problems'}
          onClick={() => setActiveBottomTab('problems')}
          badge={diagnosticCounts.errors + diagnosticCounts.warnings}
          badgeType={diagnosticCounts.errors > 0 ? 'error' : diagnosticCounts.warnings > 0 ? 'warning' : undefined}
        />
        <BottomTabButton
          tab="output"
          label="Output"
          icon={<FileOutput className="w-4 h-4" />}
          isActive={activeBottomTab === 'output'}
          onClick={() => setActiveBottomTab('output')}
        />
        <BottomTabButton
          tab="debug-console"
          label="Debug Console"
          icon={<Bug className="w-4 h-4" />}
          isActive={activeBottomTab === 'debug-console'}
          onClick={() => setActiveBottomTab('debug-console')}
        />
        <BottomTabButton
          tab="terminal"
          label="Terminal"
          icon={<Terminal className="w-4 h-4" />}
          isActive={activeBottomTab === 'terminal'}
          onClick={() => setActiveBottomTab('terminal')}
        />

        <div className="flex-1" />

        {/* Panel controls */}
        <button
          onClick={toggleBottomPanel}
          className="p-1.5 mr-2 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700 rounded transition-colors"
          title="Toggle Panel"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {activeBottomTab === 'problems' && <ProblemsPanel />}
        {activeBottomTab === 'output' && <OutputPanel />}
        {activeBottomTab === 'debug-console' && <DebugConsolePanel />}
        {activeBottomTab === 'terminal' && <TerminalPanel />}
      </div>
    </div>
  );
};
